import { FilterDocument } from '@griply/types';
import {
  getFilterDocument,
  useSmartListCustomOrderByGrouping,
  useSmartListOnReorder,
  useUpdateSmartList,
} from 'features/smart-lists';
import { useCallback, useMemo } from 'react';
import { ID } from 'shared/types/id';
import { SmartList } from 'shared/types/smart-list';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

import { SmartListEmptyState } from './smart-list-empty-state';

const groupSettings = {
  hideNewTaskRow: true,
};

type SmartListTasksOverviewProps = {
  scrollElement?: React.RefObject<HTMLDivElement>;
  smartList: SmartList;
  filterDocument?: FilterDocument;
  onReset?: () => void;
};

export const SmartListTasksOverview: React.FC<SmartListTasksOverviewProps> = ({
  scrollElement,
  smartList,
  filterDocument: providedFilterDocument,
  onReset,
}) => {
  const { submit: updateSmartList } = useUpdateSmartList();

  const filterDocument = useMemo(
    () => providedFilterDocument ?? getFilterDocument(smartList),
    [smartList, providedFilterDocument],
  );

  const onSectionCollapse = useCallback(
    (collapsedSections: ID[]) => {
      updateSmartList({
        id: smartList.id,
        filterDocument: {
          ...smartList.filterDocument,
          collapsedSections,
        },
      });
    },
    [smartList, updateSmartList],
  );

  const onReorder = useSmartListOnReorder(filterDocument, smartList);

  const customOrder = useSmartListCustomOrderByGrouping(
    filterDocument.groupByMode,
    smartList,
  );

  return (
    <ProvidedTaskFilterList
      filterDocument={filterDocument}
      defaultFilterDocument={getFilterDocument(smartList)}
      customOrder={customOrder}
      onReorder={onReorder}
      onSectionCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      groupSettingOverrides={groupSettings}
      onReset={onReset}
      EmptyState={SmartListEmptyState}
    />
  );
};
